import { useTranslation } from 'react-i18next';
import { productDisplayNameForProduct } from '@hero/core';
import type { BillingItem, Language } from '@hero/schema';

/**
 * How one charge on a Billing is named on screen.
 *
 * `BillingItem.description` is persisted audit text and it is written in
 * English on purpose — the same string goes to Moyasar's hosted page and
 * into finance exports, and it has to read identically in five years
 * (`formatBillingLine` in `@hero/core`). That makes it the wrong thing to
 * show an Arabic-speaking buyer as the row's headline, and the right
 * thing to fall back to: every Billing has a description, while
 * `purchase` is absent on anything issued before the purchase layer
 * existed.
 *
 * So the label is the product's localized name when the item declares
 * one, and the stored description otherwise. The quantity and rate are
 * rendered next to it either way, so nothing numeric is lost in the
 * swap — the full English line stays available as the row's `title`.
 */
export function useBillingItemLabel() {
  const { t, i18n } = useTranslation('m-billing');
  const language = i18n.language as Language;

  return (item: BillingItem): string => {
    const product = item.purchase?.product;
    if (!product) return item.description;
    // Empty `defaultValue` path: prefer registry name, then description.
    return (
      t(`billing_doc.product.${product}`, { defaultValue: '' }) ||
      productDisplayNameForProduct(product, language) ||
      item.description
    );
  };
}
